import { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { X, ZoomIn, ChevronDown, ChevronUp } from 'lucide-react';
import {
  Dialog,
  DialogContent,
} from "../components/ui/dialog";

const GallerySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [selectedImage, setSelectedImage] = useState(null);
  const [showMore, setShowMore] = useState(false);

  // MKD Bakery main gallery images - NEWEST photos first, with varied sizes for visual interest
  const mainGalleryImages = [
    // Featured newest photos with varied sizes
    { id: 1, url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/5uo0eq37_20.jpg', alt: 'MKD Bakery luxury blue hydrangea wedding cake', span: 'col-span-2 row-span-2' },
    { id: 2, url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/kafuhfob_13.jpg', alt: 'MKD Bakery pink tiered wedding cake with ribbons', span: '' },
    { id: 3, url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/hxyd2guo_1.PNG', alt: 'MKD Bakery elegant white wedding cake', span: '' },
    { id: 4, url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/qpjshtj7_17.jpg', alt: 'MKD Bakery Amina princess cake with crown', span: 'row-span-2' },
    { id: 5, url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/inrpgmgj_16.jpg', alt: 'MKD Bakery Will You Be My Wife proposal cake', span: 'row-span-2' },
    { id: 6, url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/i65p1ide_12.webp', alt: 'MKD Bakery chocolate drip cake', span: '' },
    { id: 7, url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/vzs12brv_15.JPG', alt: 'MKD Bakery Baby shower cake', span: '' },
    { id: 8, url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/vwmt5r47_18.jpg', alt: 'MKD Bakery Minnie Mouse Noela birthday cake', span: '' },
    { id: 9, url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/fo3a5lra_11.JPG', alt: 'MKD Bakery elegant bow cake', span: '' },
    { id: 10, url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/1b9k6pmq_14.JPG', alt: 'MKD Bakery picnic cake celebration', span: 'col-span-2' },
    { id: 11, url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/ixeqw9zd_10.JPG', alt: 'MKD Bakery candlelit birthday celebration', span: '' },
    { id: 12, url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/r990s0yr_2.webp', alt: 'MKD Bakery birthday cake gift set with wine', span: '' },
    { id: 13, url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/1fi0lhaq_3.webp', alt: 'MKD Bakery red Happy Birthday cake', span: '' },
    { id: 14, url: 'https://customer-assets-4nw71qhi.emergentagent.net/job_4358884e-87b4-4a55-8ae0-42d4124f7575/artifacts/pdniolr0_4.webp', alt: 'MKD Bakery green birthday cake', span: '' },
  ];

  // Additional gallery images (shown in expandable section) - OLDER photos
  const moreGalleryImages = [
    { id: 16, url: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/8rvh9vgm_Tall%20wedding%20cake.jpeg', alt: 'MKD Bakery tall wedding cake', span: 'md:col-span-2 md:row-span-2' },
    { id: 17, url: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/0xnfz7f0_Graduation%20cake.jpeg', alt: 'MKD Bakery graduation cake', span: '' },
    { id: 18, url: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/4cpxgaas_Joshua%20cake.jpeg', alt: 'MKD Bakery Joshua birthday cake', span: '' },
    { id: 19, url: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/vjcvcbv5_image.png', alt: 'MKD Bakery Hello 25 floral cake', span: '' },
    { id: 20, url: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/26711wdk_image.png', alt: 'MKD Bakery Barbie princess cake', span: '' },
    { id: 21, url: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/rp4gburi_image.png', alt: 'MKD Bakery red velvet heart cakes', span: 'md:col-span-2' },
    { id: 22, url: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/ah5ao06u_image.png', alt: 'MKD Bakery Forever That Girl heart cake', span: '' },
    { id: 23, url: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/n3sm3k6a_Really%20nice%20cake.jpeg', alt: 'MKD Bakery elegant cake', span: '' },
    { id: 24, url: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/nuv240yt_image.png', alt: 'MKD Bakery elegant birthday cake', span: 'md:col-span-2' },
    { id: 25, url: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/w12a4sn6_image.png', alt: 'MKD Bakery elegant ribbon cake', span: '' },
    { id: 26, url: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/nwjnqkj0_image.png', alt: 'MKD Bakery Valentine heart cakes', span: '' },
    { id: 27, url: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/ddxzxjel_image.png', alt: 'MKD Bakery Valentine cupcakes box', span: 'md:col-span-2' },
    { id: 28, url: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/nex4tu7m_image.png', alt: 'MKD Bakery berry topped cake', span: '' },
    { id: 29, url: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/izvcey0i_image.png', alt: 'MKD Bakery love you bento cake', span: '' },
    { id: 30, url: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/gl448yv6_Mamam%20cake.jpeg', alt: 'MKD Bakery birthday cake', span: '' },
    { id: 31, url: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/2z7ww0oz_image.png', alt: 'MKD Bakery Happy Birthday gold cake', span: '' },
    { id: 32, url: 'https://customer-assets.emergentagent.com/job_71f33da4-8e5c-402e-a67a-aef5c462801a/artifacts/jws2wzp9_image.png', alt: 'MKD Bakery Valentine cupcakes', span: '' },
  ];

  return (
    <section id="gallery" className="py-20 md:py-32 bg-bakery-bg dark:bg-gray-900" data-testid="gallery-section" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="font-accent text-2xl text-bakery-primary">Visual Delights</span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-bakery-text dark:text-bakery-bg mt-2">
            Our <span className="text-bakery-primary">Gallery</span>
          </h2>
          <p className="text-bakery-text/70 dark:text-bakery-bg/70 mt-4 max-w-2xl mx-auto">
            A glimpse into our delicious creations at MKD Bakery
          </p>
        </motion.div>

        {/* Main Bento Grid Gallery */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-[200px]">
          {mainGalleryImages.map((image, index) => (
            <motion.div
              key={image.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className={`relative group cursor-pointer rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 ${image.span}`}
              onClick={() => setSelectedImage(image)}
              data-testid={`gallery-image-${image.id}`}
            >
              <img
                src={image.url}
                alt={image.alt}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 gallery-image"
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                <ZoomIn 
                  size={28} 
                  className="text-white drop-shadow-lg" 
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* See More Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="flex justify-center mt-10"
        >
          <button
            onClick={() => setShowMore(!showMore)}
            className="flex items-center gap-2 bg-bakery-muted dark:bg-gray-800 hover:bg-bakery-border dark:hover:bg-gray-700 text-bakery-text dark:text-bakery-bg px-8 py-4 rounded-full font-medium transition-all duration-300 shadow-md hover:shadow-lg"
            data-testid="gallery-see-more-btn"
          >
            {showMore ? (
              <>
                <span>Show Less</span>
                <ChevronUp size={20} />
              </>
            ) : (
              <>
                <span>See More Creations</span>
                <ChevronDown size={20} />
              </>
            )}
          </button>
        </motion.div>

        {/* Expandable Gallery Section */}
        <AnimatePresence>
          {showMore && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5 }}
              className="overflow-hidden"
            >
              {/* Decorative divider with sparkle */}
              <div className="flex items-center justify-center gap-4 my-10">
                <div className="h-px bg-gradient-to-r from-transparent via-bakery-primary/50 to-transparent flex-1" />
                <span className="font-accent text-xl text-bakery-primary">✨ More Creations ✨</span>
                <div className="h-px bg-gradient-to-r from-transparent via-bakery-primary/50 to-transparent flex-1" />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[200px]">
                {moreGalleryImages.map((image, index) => (
                  <motion.div
                    key={image.id}
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className={`relative group cursor-pointer rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 ${image.span}`}
                    onClick={() => setSelectedImage(image)}
                    data-testid={`gallery-image-${image.id}`}
                  >
                    <img
                      src={image.url}
                      alt={image.alt}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 gallery-image"
                    />
                    {/* Hover overlay with gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-bakery-text/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                      <ZoomIn 
                        size={32} 
                        className="text-white drop-shadow-lg" 
                      />
                    </div>
                    {/* Subtle shine effect on hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Image Modal */}
        <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
          <DialogContent className="max-w-4xl bg-transparent border-0 p-0" data-testid="gallery-modal">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-50 p-2 bg-white/90 dark:bg-gray-800/90 rounded-full hover:bg-white dark:hover:bg-gray-800 transition-colors"
            >
              <X size={24} className="text-bakery-text dark:text-bakery-bg" />
            </button>
            {selectedImage && (
              <img
                src={selectedImage.url}
                alt={selectedImage.alt}
                className="w-full h-auto max-h-[80vh] object-contain rounded-2xl"
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default GallerySection;
